import { httpEnvelope as http } from "./http";

export type SubscriptionStatus = "trial" | "active" | "past_due" | "cancelled" | "expired";
export type BillingPeriod = "monthly" | "yearly";
export type CreditType = "credit" | "debit" | "adjustment" | "refund";

export interface PlanLimits {
  projects?: number | null;
  ai_runs?: number | null;
  credits?: number | null;
  storage_mb?: number | null;
  deploys?: number | null;
  sandboxes?: number | null;
}

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  currency: string;
  monthly_price_cents: number;
  yearly_price_cents: number;
  annual_discount_percent: number;
  trial_days: number;
  limits: PlanLimits;
  features: Record<string, boolean>;
  allowed_templates: string[];
  visibility: "public" | "private";
  is_active: boolean;
  is_global: boolean;
  is_mandatory: boolean;
  price_locked: boolean;
  min_resale_price_cents: number | null;
  max_resale_price_cents: number | null;
  sort_order: number;
}

export interface PlanInput {
  name: string;
  description?: string | null;
  monthly_price_cents: number;
  yearly_price_cents: number;
  annual_discount_percent?: number;
  trial_days?: number;
  limits?: PlanLimits;
  features?: Record<string, boolean>;
  visibility?: "public" | "private";
  is_active?: boolean;
  is_mandatory?: boolean;
  price_locked?: boolean;
  min_resale_price_cents?: number | null;
  max_resale_price_cents?: number | null;
}

export interface Subscription {
  id: string;
  status: SubscriptionStatus;
  status_label: string | null;
  billing_period: BillingPeriod;
  price_cents: number;
  currency: string;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancelled_at: string | null;
  ends_at: string | null;
  payment_provider: string | null;
  plan?: Plan;
}

export interface QuotaEntry {
  limit: number | null;
  used: number | null;
  balance?: number;
}

export interface AvailablePlan {
  offer_id: string | null;
  plan: Plan;
  monthly_price_cents: number;
  yearly_price_cents: number;
}

export interface PaymentProviderStatus {
  key: string;
  label: string;
  configured: boolean;
  status: { status: string; message: string };
}

export interface SubscriptionOverview {
  data: Subscription | null;
  meta: {
    organization: string;
    usage: Record<string, QuotaEntry>;
    credit_balance: number;
    available_plans: AvailablePlan[];
    payment_providers: PaymentProviderStatus[];
  };
}

export interface CreditEntry {
  id: string;
  type: CreditType;
  amount: number;
  balance_after: number;
  source: string;
  description: string | null;
  created_at: string;
}

export const billingService = {
  plans: () => http.get<{ data: Plan[]; meta: { limit_keys: string[] } }>("/plans"),

  createPlan: (input: PlanInput) => http.post<{ data: Plan }>("/plans", input),

  updatePlan: (id: string, input: Partial<PlanInput>) => http.put<{ data: Plan }>(`/plans/${id}`, input),

  deletePlan: (id: string) => http.delete<{ message: string }>(`/plans/${id}`),

  /** Libera a revenda de um plano para uma organização abaixo na hierarquia. */
  createOffer: (
    planId: string,
    input: { organization: string; monthly_price_cents?: number | null; yearly_price_cents?: number | null },
  ) => http.post<{ data: { id: string } }>(`/plans/${planId}/offers`, input),

  subscription: () => http.get<SubscriptionOverview>("/subscription"),

  subscribe: (input: { plan: string; billing_period: BillingPeriod; provider?: string }) =>
    http.post<{ data: Subscription; meta: { payment: unknown; payment_pending: string | null } }>(
      "/subscription",
      input,
    ),

  cancel: (id: string, reason?: string) =>
    http.post<{ data: Subscription }>(`/subscription/${id}/cancel`, { reason }),

  credits: (limit = 50) => http.get<{ data: CreditEntry[]; meta: { balance: number } }>(`/credits?limit=${limit}`),

  adjustCredits: (amount: number, description: string) =>
    http.post<{ data: CreditEntry; meta: { balance: number } }>("/credits/adjust", { amount, description }),
};
